import { ChangeDetectionStrategy, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { TemplateLayoutModel } from '@fi-app/template/template-layout.model';
import { BehaviorSubject } from 'rxjs';
import { DocumentModel } from '@fi-app/document/document.model';
import { DocumentViewService } from '@fi-app/document/document-view-service/document-view.service';
import { HtmlPreviewService } from '@fi-shared/fi-print-download/html-preview.service';
import { DocumentService } from '@fi-app/document/document.service';
import {
    DocumentGeneratePreviewService,
    TOnPreviewGeneratedFn,
} from '@fi-app/document/document-generate-preview/document-generate-preview.service';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { isDefined } from '@fi-app/shared/utils';

interface IGenerateDocumentTemplateLayout {
    templateLayout: TemplateLayoutModel;
    languageId: number;
    afterRenderer: () => void;
    documentId: number;
    //mediaBankFileReferences: IMediaBankFileReference[];
    completed$: BehaviorSubject<boolean>;
}

@Component({
    selector: 'fi-document-generate-preview',
    template: `
        @for (documentLayout of documentLayouts$ | async; track documentLayout) {
          @if (!(documentLayout.completed$ | async)) {
            <fi-document-layout
              [tplLayout]="documentLayout.templateLayout"
              [disableEdit]="true"
              [languageId]="documentLayout.languageId"
              class="view-mode"
              [attr.id]="'document_view_' + documentLayout.documentId"
              (afterViewRendered)="documentLayout.afterRenderer()"
            ></fi-document-layout>
          }
        }
        `,
    styleUrls: ['./document-generate-preview.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-document-generate-preview' },
    standalone: false,
})
export class DocumentGeneratePreviewComponent implements OnInit {
    documentLayouts$ = new BehaviorSubject<IGenerateDocumentTemplateLayout[]>([]);

    constructor(
        private documentViewService: DocumentViewService,
        private htmlPreviewService: HtmlPreviewService,
        private documentService: DocumentService,
        private documentGeneratePreviewService: DocumentGeneratePreviewService,
    ) {}

    ngOnInit() {
        this.documentGeneratePreviewService.registerGeneratePreview(
            (doc, languageId, onPreviewGeneratedFn: TOnPreviewGeneratedFn) => {
                if (!isDefined(onPreviewGeneratedFn)) {
                    onPreviewGeneratedFn = (documentId: number, previewHTML: string) => {
                        return this.documentService.GenerateDocumentPreviewFileImage(doc.Id, previewHTML);
                    };
                }

                return this.generateDocumentPreviewImage(doc, languageId, onPreviewGeneratedFn);
            },
        );
    }

    private generateDocumentPreviewImage(
        doc: DocumentModel,
        languageId: number,
        onPreviewGeneratedFn: TOnPreviewGeneratedFn,
    ) {
        let templateLayout: IGenerateDocumentTemplateLayout;
        return new Promise<void>((resolve, reject) => {
            this.documentViewService.prepareEverythingForView(doc, languageId).then((documentViewData) => {
                templateLayout = {
                    documentId: doc.Id,
                    //mediaBankFileReferences: doc.MediaBankFileReferences,
                    templateLayout: documentViewData.templateLayout,
                    languageId: documentViewData.languageId,
                    completed$: new BehaviorSubject(false),
                    afterRenderer: () => {
                        setTimeout(() => {
                            const cleanupFn = () => {
                                const currentLayouts = this.documentLayouts$.value;
                                currentLayouts.splice(currentLayouts.indexOf(templateLayout), 1);
                                this.documentLayouts$.next(currentLayouts);
                            };
                            try {
                                const previewHTML = this.htmlPreviewService.getPreviewHtml(
                                    document.getElementById('document_view_' + doc.Id),
                                );

                                onPreviewGeneratedFn(doc.Id, previewHTML)
                                    .then(() => {
                                        resolve();
                                    }, reject)
                                    .finally(() => {
                                        cleanupFn();
                                    });
                            } catch (e) {
                                cleanupFn();
                                reject(e);
                            }
                        }, 3000); // should wait while images have been loaded, todo find better way
                    },
                };
                const currentLayouts = this.documentLayouts$.value;
                currentLayouts.push(templateLayout);
                this.documentLayouts$.next(currentLayouts);
            }, reject);
        }).finally(() => {
            if (templateLayout) templateLayout.completed$.next(true);
        });
    }
}
